import { CheckCircle2, XSquare, X } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { materialWarning, type BatchPreview } from './batch'

// Sticky bar shown only when at least one row is selected. It reports the
// count, a live minor/material summary from previewCorrectionBatch, the exact
// amber material warning when any material rows are selected, and the three
// actions. "Approve selected" opens the confirm dialog — it never applies
// directly.
export function BatchActionBar({
  count,
  preview,
  previewLoading,
  onApprove,
  onClose,
  onClear,
}: {
  count: number
  preview: BatchPreview | null
  previewLoading: boolean
  onApprove: () => void
  onClose: () => void
  onClear: () => void
}) {
  const materialCount = preview?.material_count ?? 0
  const minorCount = preview?.minor_count ?? 0

  return (
    <div className="sticky bottom-0 z-10 -mx-4 border-t border-border bg-card/95 px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-sm font-semibold text-foreground">
          <span className="tabular-nums">{count}</span> selected
        </span>

        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
          {previewLoading && !preview ? (
            <>
              <Spinner className="h-3.5 w-3.5" /> summarising…
            </>
          ) : (
            <span className="tabular-nums">
              {minorCount} minor, {materialCount} material
            </span>
          )}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" className="gap-1.5" onClick={onApprove}>
            <CheckCircle2 className="h-4 w-4" />
            Approve selected
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={onClose}>
            <XSquare className="h-4 w-4" />
            Close selected
          </Button>
          <Button size="sm" variant="ghost" className="gap-1.5" onClick={onClear}>
            <X className="h-4 w-4" />
            Clear selection
          </Button>
        </div>
      </div>

      {materialCount > 0 && preview ? (
        <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          {materialWarning(preview.owners_affected, preview.owner_money_moved_zar)}
        </p>
      ) : null}
    </div>
  )
}
